import * as React from 'react';

import { cn } from '../utils/cn';

export type SwitchProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> & {
  onCheckedChange?: (checked: boolean) => void;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

export const Switch = React.forwardRef<HTMLInputElement, SwitchProps>(
  ({ className, checked, defaultChecked, disabled, onCheckedChange, ...props }, ref) => {
    const isControlled = typeof checked === 'boolean';
    const [uncontrolledChecked, setUncontrolledChecked] = React.useState(Boolean(defaultChecked));
    const resolvedChecked = isControlled ? checked : uncontrolledChecked;

    return (
      <label
        className={cn(
          'relative inline-flex h-5 w-9 select-none items-center',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className
        )}
      >
        <input
          {...props}
          ref={ref}
          type="checkbox"
          className="sr-only"
          disabled={disabled}
          checked={isControlled ? checked : undefined}
          defaultChecked={!isControlled ? defaultChecked : undefined}
          onChange={event => {
            if (!isControlled) {
              setUncontrolledChecked(event.target.checked);
            }
            onCheckedChange?.(event.target.checked);
            props.onChange?.(event);
          }}
        />
        <span
          aria-hidden
          className={cn(
            'block h-5 w-9 rounded-full border transition-colors',
            resolvedChecked ? 'border-primary bg-primary' : 'border-border bg-muted'
          )}
        />
        <span
          aria-hidden
          className={cn(
            'pointer-events-none absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-background shadow transition-transform',
            resolvedChecked ? 'translate-x-4' : 'translate-x-0'
          )}
        />
      </label>
    );
  }
);

Switch.displayName = 'Switch';
